import { outputPortCurrent } from "./output-current-limits";
import { effectiveChargeCurrent } from "./charge-current";
import { computeLiveCircuitFlow } from "./live-cable-flow";
import { isFuseBlockLike } from "./auto-fuse";
import { isPassthroughComponent, type SimulateContext } from "./cable-current";
import type { DefLookup } from "./catalog";
import { componentLoadCurrents, dcDcInputCurrent } from "./component-current";
import { buildGraph, reachable, splitV, v, type Graph, type Vertex } from "./diagnostics/graph";
import { isJunction } from "./node-kind";
import { indexNodesById } from "./node-tree";
import { isSenseCable } from "./sense-terminal";
import { applyStateToAmp, getEffectiveStateOption } from "./simulate/states";
import type { Cable, ComponentDefinition, ComponentNode, System, SystemNode } from "./types";

/**
 * A battery declares neither `ampDraw` nor `ampOutput`, so `cable-current.ts`
 * reports 0 A on an interconnect. This module supplies the missing current.
 */
export type BatteryBankCurrents = {
  /** cableId → signed continuous current (A), positive `fromNodeId` → `toNodeId`.
   *  A missing cable is one the model declined to solve, not one carrying 0 A. */
  byCable: ReadonlyMap<string, number>;
  /** batteryNodeId → net current (A). Positive = charging, negative = discharging. */
  byBattery: ReadonlyMap<string, number>;
  /** cableId → unsigned current (A) carried onward from the posts until the run
   *  divides at a bus. `diagnostics/sizing.ts` folds it into `demand` as a floor,
   *  never a substitute the way it does {@link byCable}. */
  trunkByCable: ReadonlyMap<string, number>;
};

const EMPTY: BatteryBankCurrents = {
  byCable: new Map(),
  byBattery: new Map(),
  trunkByCable: new Map(),
};

type Rail = "positive" | "negative";

type BatteryInfo = {
  node: ComponentNode;
  def: ComponentDefinition;
  capacityAh: number;
  terms: Record<Rail, string[]>;
};

function isBusPlumbing(node: SystemNode, def: ComponentDefinition | undefined): boolean {
  if (isJunction(node, def)) return true;
  if (!def) return false;
  return isPassthroughComponent(def) || isFuseBlockLike(def);
}

function terminalsByRail(def: ComponentDefinition, rail: Rail): string[] {
  return def.terminals.filter((t) => t.polarity === rail).map((t) => t.id);
}

/** Signed contribution of one non-battery leaf on the bank's positive bus:
 *  positive charges the bank, negative discharges it. */
function leafContribution(
  node: ComponentNode,
  def: ComponentDefinition,
  busTerminalIds: string[],
  simulate: SimulateContext | undefined,
  system: System,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
): number {
  const opt = simulate ? getEffectiveStateOption(def, node.id, simulate) : undefined;
  if (opt?.ampOverride === 0) return 0;
  if (simulate?.energizedTerminals) {
    const anyEnergized = busTerminalIds.some((t) =>
      simulate.energizedTerminals!.has(`${node.id}:${t}`),
    );
    if (!anyEnergized) return 0;
  }
  const directions = new Set(
    busTerminalIds
      .map((id) => def.terminals.find((t) => t.id === id)?.direction)
      .filter((d): d is NonNullable<typeof d> => d != null),
  );
  const cat = def.category;

  if (cat === "solar" || cat === "source" || cat === "engine") {
    return effectiveChargeCurrent(node, def, simulate) ?? 0;
  }
  if (cat === "charger" && directions.has("output")) {
    const output = effectiveChargeCurrent(node, def, simulate) ?? 0;
    // Credit only outputs reaching this bank, retaining the aggregate cap.
    const connectedRating = def.terminals.filter((term) =>
      term.direction === "output" && term.polarity === "positive" && busTerminalIds.includes(term.id))
      .reduce((total, term) => total + outputPortCurrent(def, term.id, output), 0);
    return Math.min(output, connectedRating);
  }
  if (cat === "charger" && directions.has("input")) {
    const output = effectiveChargeCurrent(node, def, simulate) ?? 0;
    const draw =
      applyStateToAmp(def.ampDraw, opt, "draw") ??
      dcDcInputCurrent(def, node.id, nodesById, system.cables, defLookup, output, simulate) ?? output;
    return -(draw ?? 0);
  }
  if (cat === "load") {
    const { draw } = componentLoadCurrents(def, node.id, nodesById, system.cables, defLookup, simulate);
    return -(applyStateToAmp(draw, opt, "draw") ?? 0);
  }
  return 0;
}

/** Batteries grouped by the positive bus they share, plus that bus's net
 *  current. Single batteries are included — the trunk walk needs them. */
function findBanks(
  system: System,
  defLookup: DefLookup,
  simulate: SimulateContext | undefined,
): {
  banks: Array<{ members: BatteryInfo[]; netCurrentA: number }>;
  graph: Graph | null;
  /** Every battery in the system — the trunk walk stops at any battery post,
   *  not just this bank's. */
  batteryIds: Set<string>;
} {
  const batteries: BatteryInfo[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || def.category !== "battery") continue;
    batteries.push({
      node: node as ComponentNode,
      def,
      capacityAh: def.capacityAh ?? 0,
      terms: {
        positive: terminalsByRail(def, "positive"),
        negative: terminalsByRail(def, "negative"),
      },
    });
  }
  if (batteries.length === 0) {
    return { banks: [], graph: null, batteryIds: new Set() };
  }

  // Sense leads out: two batteries on one BMV B+ post are two banks, not one
  // bank with an 8 A interconnect through a 1 A wire.
  const graph = buildGraph(system, defLookup, { simulate, excludeSense: true });
  const byId = new Map(batteries.map((b) => [b.node.id, b]));
  const nodesById = new Map(system.nodes.map((n) => [n.id, n]));
  const currentNodesById = indexNodesById(system.nodes);

  const seen = new Set<string>();
  const banks: Array<{ members: BatteryInfo[]; netCurrentA: number }> = [];
  for (const b of batteries) {
    if (seen.has(b.node.id)) continue;
    const busVerts = new Set<string>();
    for (const t of b.terms.positive) {
      for (const vert of reachable(graph, v(b.node.id, t))) busVerts.add(vert);
    }
    const members: BatteryInfo[] = [];
    // Grouped per node, so a multi-terminal leaf is counted once.
    const busTerminalsByNode = new Map<string, string[]>();
    for (const vert of busVerts) {
      const sep = vert.lastIndexOf(":");
      const nodeId = vert.slice(0, sep);
      const termId = vert.slice(sep + 1);
      const list = busTerminalsByNode.get(nodeId);
      if (list) list.push(termId);
      else busTerminalsByNode.set(nodeId, [termId]);
    }
    for (const [nodeId, termIds] of busTerminalsByNode) {
      const member = byId.get(nodeId);
      // Reaching the next battery's negative post is a series link, not a
      // parallel member sharing this positive bus.
      if (member && !seen.has(nodeId) && member.terms.positive.some((id) => termIds.includes(id))) {
        seen.add(nodeId);
        members.push(member);
      }
    }
    if (members.length === 0) continue;

    let sources = 0;
    let sinks = 0;
    for (const [nodeId, termIds] of busTerminalsByNode) {
      if (byId.has(nodeId)) continue;
      const node = nodesById.get(nodeId);
      if (!node || node.kind !== "component") continue;
      const def = defLookup(node.definitionId, node.customDefinition);
      if (!def || isBusPlumbing(node, def)) continue;
      const contribution = leafContribution(node as ComponentNode, def, termIds, simulate, system, currentNodesById, defLookup);
      if (contribution > 0) sources += contribution;
      else sinks += -contribution;
    }
    // Edit mode takes sinks only: sizing must not assume anything is charging.
    const netCurrentA = simulate ? sources - sinks : -sinks;
    banks.push({ members, netCurrentA });
  }
  return { banks, graph, batteryIds: new Set(byId.keys()) };
}

function interconnects(
  cables: readonly Cable[],
  members: BatteryInfo[],
  rail: Rail,
): Array<{ cable: Cable; a: string; b: string }> {
  const byNode = new Map(members.map((m) => [m.node.id, m]));
  const out: Array<{ cable: Cable; a: string; b: string }> = [];
  for (const c of cables) {
    if (c.fromNodeId === c.toNodeId) continue;
    const from = byNode.get(c.fromNodeId);
    const to = byNode.get(c.toNodeId);
    if (!from || !to) continue;
    if (!from.terms[rail].includes(c.fromTerminalId)) continue;
    if (!to.terms[rail].includes(c.toTerminalId)) continue;
    out.push({ cable: c, a: c.fromNodeId, b: c.toNodeId });
  }
  return out;
}

function feeders(
  cables: readonly Cable[],
  members: BatteryInfo[],
  rail: Rail,
): Array<{ cable: Cable; batteryId: string; batteryIsFrom: boolean }> {
  const byNode = new Map(members.map((m) => [m.node.id, m]));
  const out: Array<{ cable: Cable; batteryId: string; batteryIsFrom: boolean }> = [];
  for (const c of cables) {
    const from = byNode.get(c.fromNodeId);
    const to = byNode.get(c.toNodeId);
    const fromOnRail = from?.terms[rail].includes(c.fromTerminalId) ?? false;
    const toOnRail = to?.terms[rail].includes(c.toTerminalId) ?? false;
    if (fromOnRail === toOnRail) continue; // interconnect, or unrelated
    out.push({
      cable: c,
      batteryId: fromOnRail ? c.fromNodeId : c.toNodeId,
      batteryIsFrom: fromOnRail,
    });
  }
  return out;
}

/** Battery ids reachable from `link`'s `b` end without crossing `link`. */
function farSide(
  link: { cable: Cable; a: string; b: string },
  links: Array<{ cable: Cable; a: string; b: string }>,
): Set<string> {
  const adj = new Map<string, string[]>();
  const link2 = (from: string, to: string) => {
    const list = adj.get(from);
    if (list) list.push(to);
    else adj.set(from, [to]);
  };
  for (const l of links) {
    if (l.cable.id === link.cable.id) continue;
    link2(l.a, l.b);
    link2(l.b, l.a);
  }
  const out = new Set<string>([link.b]);
  const queue = [link.b];
  while (queue.length) {
    const cur = queue.shift()!;
    for (const next of adj.get(cur) ?? []) {
      if (out.has(next)) continue;
      out.add(next);
      queue.push(next);
    }
  }
  return out;
}

/**
 * Sizing current for an interconnect in edit mode. A tripped BMS or an ageing
 * cell pushes the whole draw onto the remaining batteries, so the basis is what
 * the stranded ones can deliver (`maxDischargeA`), capped by the bank's draw.
 * Undeclared `maxDischargeA` means the whole bank draw.
 */
function worstCaseLinkCurrent(stranded: BatteryInfo[], bankNetCurrentA: number): number {
  const busDraw = Math.abs(bankNetCurrentA);
  let ceiling = 0;
  for (const m of stranded) {
    if (m.def.maxDischargeA == null) return busDraw;
    ceiling += m.def.maxDischargeA;
  }
  return Math.min(busDraw, ceiling);
}

/** A real return crosses a shunt, a switch, an inline fuse; a longer chain is a
 *  topology this model has no business guessing at. */
const MAX_TRUNK_HOPS = 8;

/** The terminals galvanically common with `start` inside its own component. */
function galvanicGroup(graph: Graph, start: Vertex): Set<Vertex> {
  const seen = new Set<Vertex>([start]);
  const queue = [start];
  while (queue.length) {
    const cur = queue.shift()!;
    for (const { vertex, via } of graph.adj.get(cur) ?? []) {
      if (via.kind !== "intra" || seen.has(vertex)) continue;
      seen.add(vertex);
      queue.push(vertex);
    }
  }
  return seen;
}

type TrunkStep = { cableId: string; at: Vertex; currentA: number };

/**
 * Carry the bank's current onward from its battery posts through the series
 * plumbing (shunt, switch, inline fuse) nothing else in the engine can size.
 *
 * It stops where the answer stops being forced: two ways out of a galvanic group
 * means a bus, dividing by impedances this model does not have. The cap at
 * `|netCurrentA|` keeps the return equal to the feed rather than a multiple.
 */
function creditBankTrunk(
  graph: Graph,
  seeds: TrunkStep[],
  bankTotalA: number,
  batteryIds: ReadonlySet<string>,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
  out: Map<string, number>,
): void {
  if (bankTotalA <= 0) return;
  const visited = new Set(seeds.map((s) => s.cableId));
  let frontier = seeds;
  for (let hop = 0; hop < MAX_TRUNK_HOPS && frontier.length > 0; hop++) {
    const groups = new Map<
      string,
      { verts: Set<Vertex>; currentA: number; incoming: Set<string> }
    >();
    for (const step of frontier) {
      const verts = galvanicGroup(graph, step.at);
      const key = [...verts].sort().join("|");
      const g = groups.get(key);
      if (g) {
        g.currentA += step.currentA;
        g.incoming.add(step.cableId);
      } else {
        groups.set(key, {
          verts,
          currentA: step.currentA,
          incoming: new Set([step.cableId]),
        });
      }
    }
    const next: TrunkStep[] = [];
    for (const g of groups.values()) {
      // A battery post means the run closed back on a bank; no trunk past it.
      let atLeaf = false;
      for (const vert of g.verts) {
        const node = nodesById[splitV(vert).nodeId];
        if (!node) continue;
        const def = node.kind === "component" ? defLookup(node.definitionId, node.customDefinition) : undefined;
        if (batteryIds.has(node.id) || !isBusPlumbing(node, def)) atLeaf = true;
      }
      // An active device can add/remove current at a shared terminal. A
      // converter's common negative is not passive series plumbing: input
      // losses must not be carried onward into its output return.
      if (atLeaf) continue;
      const exits = new Map<string, Vertex>();
      for (const vert of g.verts) {
        for (const { vertex, via } of graph.adj.get(vert) ?? []) {
          if (via.kind !== "cable") continue;
          if (g.incoming.has(via.cableId) || visited.has(via.cableId)) continue;
          exits.set(via.cableId, vertex);
        }
      }
      if (exits.size !== 1) continue;
      const [cableId, far] = [...exits][0];
      visited.add(cableId);
      const carried = Math.min(g.currentA, bankTotalA);
      out.set(cableId, Math.max(out.get(cableId) ?? 0, carried));
      next.push({ cableId, at: far, currentA: carried });
    }
    frontier = next;
  }
}

/** Keyed by object identity: a `System` mutated in place serves a stale answer. */
const bankCache = new WeakMap<System, WeakMap<DefLookup, WeakMap<object, BatteryBankCurrents>>>();
const EDIT_MODE_KEY = {};

export function computeBatteryBankCurrents(
  system: System,
  defLookup: DefLookup,
  simulate?: SimulateContext,
): BatteryBankCurrents {
  let byDefinition = bankCache.get(system);
  if (!byDefinition) {
    byDefinition = new WeakMap();
    bankCache.set(system, byDefinition);
  }
  let bySimulate = byDefinition.get(defLookup);
  if (!bySimulate) {
    bySimulate = new WeakMap();
    byDefinition.set(defLookup, bySimulate);
  }
  const key = simulate ?? EDIT_MODE_KEY;
  const cached = bySimulate.get(key);
  if (cached) return cached;
  const result = computeBatteryBankCurrentsUncached(system, defLookup, simulate);
  bySimulate.set(key, result);
  return result;
}

function computeBatteryBankCurrentsUncached(
  system: System,
  defLookup: DefLookup,
  simulate?: SimulateContext,
): BatteryBankCurrents {
  if (simulate) {
    const flow = computeLiveCircuitFlow(indexNodesById(system.nodes), system.cables, defLookup, simulate);
    return { byCable: flow.byCable, byBattery: flow.byBattery, trunkByCable: new Map([...flow.byCable].map(([id, current]) => [id, Math.abs(current)])) };
  }
  const { banks, graph, batteryIds } = findBanks(system, defLookup, simulate);
  if (banks.length === 0 || !graph) return EMPTY;

  // A sense wire on a battery post is not a feeder of the bank.
  const nodesById = indexNodesById(system.nodes);
  const cables = system.cables.filter((c) => !isSenseCable(c, nodesById, defLookup));

  const byCable = new Map<string, number>();
  const byBattery = new Map<string, number>();
  const trunkByCable = new Map<string, number>();

  for (const bank of banks) {
    const { members, netCurrentA } = bank;
    const totalCapacity = members.reduce((sum, m) => sum + m.capacityAh, 0);
    const shareOf = (m: BatteryInfo): number =>
      totalCapacity > 0
        ? netCurrentA * (m.capacityAh / totalCapacity)
        : netCurrentA / members.length;

    // `byCable`/`byBattery` stay closed to a lone battery: its feeder has a real
    // leaf the per-endpoint walk resolves. The trunk walk below runs either way.
    const isBank = members.length >= 2;
    if (isBank) for (const m of members) byBattery.set(m.node.id, shareOf(m));
    if (netCurrentA === 0) continue;

    for (const rail of ["positive", "negative"] as const) {
      const links = interconnects(cables, members, rail);
      const taps = feeders(cables, members, rail);
      // The negative rail returns into the post: same magnitude, opposite sense.
      const railSign = rail === "positive" ? 1 : -1;

      if (!isBank) {
        // No sharing model for a lone battery — fall through to the trunk.
      } else if (links.length === 0) {
        // Star-wired: each feeder carries just that battery's share.
        for (const t of taps) {
          const member = members.find((m) => m.node.id === t.batteryId);
          const share = byBattery.get(t.batteryId) ?? 0;
          if (!member || share === 0) continue;
          const ownTaps = taps.filter((x) => x.batteryId === t.batteryId).length;
          const magnitude = simulate
            ? Math.abs(share) / ownTaps
            : worstCaseLinkCurrent([member], netCurrentA) / ownTaps;
          // Discharging leaves the post on +, returns on −; `batteryIsFrom` maps
          // that onto the cable's ends.
          const flowSign = -Math.sign(share) * railSign * (t.batteryIsFrom ? 1 : -1);
          byCable.set(t.cable.id, magnitude * flowSign);
        }
      } else if (taps.length === 1) {
        // Daisy chain: with one way out, each interconnect carries the shares
        // stranded behind it. Two taps or a ring is under-determined, and left
        // to the per-endpoint resolver.
        const tap = taps[0];

        for (const link of links) {
          // The side the tap is NOT on holds the batteries whose current has to
          // cross this link.
          const toSide = farSide(link, links);
          const tapOnToSide = toSide.has(tap.batteryId);
          const stranded = members.filter(
            (m) => toSide.has(m.node.id) !== tapOnToSide,
          );
          const strandedShare = stranded.reduce(
            (sum, m) => sum + (byBattery.get(m.node.id) ?? 0),
            0,
          );
          if (strandedShare === 0) continue;
          const magnitude = simulate
            ? Math.abs(strandedShare)
            : worstCaseLinkCurrent(stranded, netCurrentA);
          // Direction comes from the share's sign; it inverts when the stranded
          // side is the cable's `from` end.
          const flowSign = Math.sign(tapOnToSide ? -strandedShare : strandedShare);
          byCable.set(link.cable.id, magnitude * flowSign * railSign);
        }

        const magnitude = -netCurrentA * railSign;
        byCable.set(tap.cable.id, tap.batteryIsFrom ? magnitude : -magnitude);
      }

      // Seeded with each tap's conserved share, not the worst case in `byCable`:
      // summing worst cases sizes the trunk at double what can flow.
      const trunkSeeds: TrunkStep[] = [];
      for (const t of taps) {
        const ownTaps = taps.filter((x) => x.batteryId === t.batteryId).length;
        // A daisy chain leaves through one cable carrying the whole bank; a
        // star splits it per tap.
        const share = isBank
          ? Math.abs(byBattery.get(t.batteryId) ?? 0)
          : Math.abs(netCurrentA);
        const carried =
          links.length === 0 ? share / ownTaps : Math.abs(netCurrentA);
        if (carried <= 0) continue;
        trunkSeeds.push({
          cableId: t.cable.id,
          at: t.batteryIsFrom
            ? v(t.cable.toNodeId, t.cable.toTerminalId)
            : v(t.cable.fromNodeId, t.cable.fromTerminalId),
          currentA: carried,
        });
      }
      creditBankTrunk(
        graph,
        trunkSeeds,
        Math.abs(netCurrentA),
        batteryIds,
        nodesById,
        defLookup,
        trunkByCable,
      );
    }
  }

  return { byCable, byBattery, trunkByCable };
}
